package com.schoolconnect.app.feature.quickannouncements

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.list.*
import com.schoolconnect.app.feature.common.*
import kotlinx.coroutines.delay
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.painterResource

private val QaOrange = Color(0xFFE88900)
private val QaOrangeSoft = Color(0xFFFFF3E2)
private val QaBlueSoft = Color(0xFFEAF1FF)
private val QaGreySoft = Color(0xFFF1F2F5)

@Composable
fun QuickAnnouncementsScreen(
    state: QuickAnnouncementsUiState,
    onEvent: (QuickAnnouncementsUiEvent) -> Unit,
    shellRoleCode: String,
    onChangeRole: () -> Unit,
    onLogout: () -> Unit,
    onBack: () -> Unit
) {
    var profileExpanded by remember { mutableStateOf(false) }
    var rowsMenu by remember { mutableStateOf(false) }
    var addVisible by remember { mutableStateOf(false) }
    var editRow by remember { mutableStateOf<QuickAnnouncementRow?>(null) }
    var viewRow by remember { mutableStateOf<QuickAnnouncementRow?>(null) }
    var duplicateRow by remember { mutableStateOf<QuickAnnouncementRow?>(null) }
    var endNowRow by remember { mutableStateOf<QuickAnnouncementRow?>(null) }
    var deleteRow by remember { mutableStateOf<QuickAnnouncementRow?>(null) }
    LaunchedEffect(state.saveGeneration) {
        if (state.saveGeneration > 0) {
            delay(150)
            addVisible = false
            editRow = null
        }
    }
    val window = pageWindow(state.pageIndex, state.pageSize, state.totalCount)
    val pageItems = state.rows

    AdminListPageScaffold(
        title = "Quick Announcements",
        subtitle = "Create and manage short, time-bound announcements for parents.",
        backgroundColor = AppBackground,
        titleColor = AppPageHeader,
        subtitleColor = AppPageSubtitle,
        headerActions = { ShellTopRightActions(shellRoleLabel(shellRoleCode), shellRoleSubtitle(shellRoleCode), shellAvatarInitials(shellRoleLabel(shellRoleCode)), profileExpanded, { profileExpanded = it }, onChangeRole, onLogout) },
        summaryCards = {
            if (state.isLoading) {
                AdminListSummaryCardsShimmer(cardCount = 4)
            } else {
                QuickMetric("Total Announcements", state.totalAnnouncements.toString(), "All announcements", Res.drawable.megaphone, AppPrimary, QaBlueSoft)
                QuickMetric("Active", state.activeCount.toString(), "Currently displayed", Res.drawable.circle_check, AppSuccess, AppSuccessSoft)
                QuickMetric("Expired", state.expiredCount.toString(), "Display ended", Res.drawable.clock, AppNavy, QaGreySoft)
                QuickMetric("Drafts", state.draftCount.toString(), "Not published", Res.drawable.edit, QaOrange, QaOrangeSoft)
            }
        },
        toolbar = {
            QuickAnnouncementFilters(state, onEvent)
            if (state.saveErrorMessage.isNotBlank()) {
                Text(state.saveErrorMessage, color = AppDanger, modifier = Modifier.padding(top = 8.dp, start = 4.dp))
            }
            if (state.actionErrorMessage.isNotBlank()) {
                Text(state.actionErrorMessage, color = AppDanger, modifier = Modifier.padding(top = 8.dp, start = 4.dp))
            }
        },
        table = { modifier ->
            AdminListTableCard(
                modifier = modifier,
                minContentWidth = 1060.dp,
                header = { AdminListSortHeaderRow(listOf(
                    AdminListSortColumn("QA ID", .85f, state.sortBy == "qa_id", state.sortAscending) { onEvent(QuickAnnouncementsUiEvent.SortChanged("qa_id")) },
                    AdminListSortColumn("Message", 2.15f, state.sortBy == "message", state.sortAscending) { onEvent(QuickAnnouncementsUiEvent.SortChanged("message")) },
                    AdminListSortColumn("Display From", 1.05f, state.sortBy == "created_at", state.sortAscending) { onEvent(QuickAnnouncementsUiEvent.SortChanged("created_at")) },
                    AdminListSortColumn("Display Until", 1.05f, state.sortBy == "display_until", state.sortAscending) { onEvent(QuickAnnouncementsUiEvent.SortChanged("display_until")) },
                    AdminListSortColumn("Status", .75f, state.sortBy == "status", state.sortAscending) { onEvent(QuickAnnouncementsUiEvent.SortChanged("status")) }
                ), actionsLabel = "Actions", actionsWeight = .95f, actionsAlignment = Alignment.Center) },
                body = {
                    if (state.isLoading) AdminListTableShimmer(
                        columns = listOf(
                            AdminListHeaderColumn("QA ID", .85f),
                            AdminListHeaderColumn("Message", 2.15f),
                            AdminListHeaderColumn("Display From", 1.05f),
                            AdminListHeaderColumn("Display Until", 1.05f),
                            AdminListHeaderColumn("Status", .75f),
                            AdminListHeaderColumn("Actions", .95f)
                        ),
                        rowCount = state.pageSize,
                        modifier = Modifier.fillMaxWidth().weight(1f)
                    )
                    else if (state.errorMessage.isNotBlank()) Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { AppDialogErrorMessage(state.errorMessage) }
                    else if (pageItems.isEmpty()) Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { Text("No announcements found", color = AppNavy, fontWeight = FontWeight.Bold) }
                    else AdminListTableBody(items = pageItems, key = { it.id }, modifier = Modifier.fillMaxWidth().weight(1f)) { row ->
                        QuickAnnouncementRowView(row, onView = { viewRow = row }, onEdit = { editRow = row }, onDuplicate = { duplicateRow = row }, onEndNow = { endNowRow = row }, onDelete = { deleteRow = row }, actionsEnabled = !state.isMutating)
                        if (row != pageItems.last()) HorizontalDivider(color = AppBorder)
                    }
                },
                footer = { AdminListFooter(pageText = pageFooterText(window, "announcements"), pageIndex = window.pageIndex, pageCount = window.pageCount, onPageChanged = { onEvent(QuickAnnouncementsUiEvent.PageChanged(it)) }, pageSize = state.pageSize, rowsPerPageExpanded = rowsMenu, onRowsPerPageToggle = { rowsMenu = !rowsMenu }, onRowsPerPageDismiss = { rowsMenu = false }, onPageSizeChanged = { onEvent(QuickAnnouncementsUiEvent.PageSizeChanged(it)) }) }
            )
        },
        bottomAction = { Spacer(Modifier.weight(1f)); AdminListPrimaryActionButton("Add Announcement", painterResource(Res.drawable.plus), { addVisible = true }, Modifier.padding(top = 4.dp)) }
    )

    AddQuickAnnouncementDialog(visible = addVisible, onDismiss = { addVisible = false }, onSubmit = { draft -> onEvent(QuickAnnouncementsUiEvent.CreateRequested(draft)) }, isSaving = state.isSaving)
    EditQuickAnnouncementDialog(row = editRow, onDismiss = { editRow = null }, onSubmit = { _, draft -> editRow?.let { onEvent(QuickAnnouncementsUiEvent.UpdateRequested(it.backendId, it.status, draft)) } }, isSaving = state.isSaving)
    ViewQuickAnnouncementDialog(row = viewRow, onDismiss = { viewRow = null })
    duplicateRow?.let { row ->
        AppConfirmationDialog(
            onDismiss = { if (!state.isMutating) duplicateRow = null },
            title = "Duplicate Announcement?",
            subtitle = "Create a copy of this announcement.",
            bodyText = "Duplicate ${row.id}?",
            confirmLabel = if (state.isMutating) "Duplicating…" else "Duplicate",
            confirmEnabled = !state.isMutating,
            dismissEnabled = !state.isMutating,
            tone = AppConfirmationDialogTone.Primary,
            onConfirm = {
                duplicateRow = null
                onEvent(QuickAnnouncementsUiEvent.DuplicateRequested(row.backendId))
            }
        )
    }
    endNowRow?.let { row ->
        AppConfirmationDialog(
            onDismiss = { if (!state.isMutating) endNowRow = null },
            title = "End Announcement Now?",
            subtitle = "Confirm this action.",
            bodyText = "This announcement will no longer be visible to parents.",
            confirmLabel = if (state.isMutating) "Ending…" else "End Now",
            confirmEnabled = !state.isMutating,
            dismissEnabled = !state.isMutating,
            tone = AppConfirmationDialogTone.Danger,
            onConfirm = {
                endNowRow = null
                onEvent(QuickAnnouncementsUiEvent.EndNowRequested(row.backendId))
            }
        )
    }
    deleteRow?.let { row ->
        AppConfirmationDialog(
            onDismiss = { if (!state.isMutating) deleteRow = null },
            title = "Delete Announcement?",
            subtitle = "This action cannot be undone.",
            bodyText = "Delete ${row.id}?\n${row.message}",
            confirmLabel = if (state.isMutating) "Deleting…" else "Delete",
            confirmEnabled = !state.isMutating,
            dismissEnabled = !state.isMutating,
            tone = AppConfirmationDialogTone.Danger,
            onConfirm = {
                deleteRow = null
                onEvent(QuickAnnouncementsUiEvent.DeleteRequested(row.backendId))
            }
        )
    }
}

@Composable private fun RowScope.QuickMetric(title: String, value: String, subtitle: String, icon: org.jetbrains.compose.resources.DrawableResource, accent: Color, bg: Color) = AdminListSummaryCard(title, value, subtitle, painterResource(icon), accent, bg, AppMuted, modifier = Modifier.weight(1f))

@Composable private fun QuickAnnouncementFilters(state: QuickAnnouncementsUiState, onEvent: (QuickAnnouncementsUiEvent) -> Unit) {
    AppCard(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(10.dp)), containerColor = Color.White) {
        Column(Modifier.fillMaxWidth().padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Bottom) {
                Column(Modifier.weight(1.45f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                    Text("Search by QA ID / Message", color = AppNavy, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                    BoxWithConstraints(Modifier.fillMaxWidth()) { AdminListSearchField(state.searchQuery, { onEvent(QuickAnnouncementsUiEvent.SearchChanged(it)) }, "Search...", width = maxWidth, height = 44.dp, surfaceColor = Color.White, borderColor = AppBorder, textColor = AppNavy) }
                }
                QaDropdown("Status", state.status.ifBlank { "All Statuses" }, listOf("Active", "Expired", "Draft"), Modifier.weight(1f)) { onEvent(QuickAnnouncementsUiEvent.StatusChanged(it)) }
                QaDropdown("Created By", state.createdBy.ifBlank { "All Users" }, state.rows.map { it.createdBy }.distinct(), Modifier.weight(1f)) { selected -> onEvent(QuickAnnouncementsUiEvent.CreatedByChanged(selected, state.rows.firstOrNull { it.createdBy == selected }?.createdById)) }
                QaDropdown("Period", state.period, listOf("Today", "Last 7 Days", "This Month"), Modifier.weight(.9f)) { onEvent(QuickAnnouncementsUiEvent.PeriodChanged(it)) }
                AdminListToolbarButton(label = "Clear", icon = painterResource(Res.drawable.refresh_cw), onClick = { onEvent(QuickAnnouncementsUiEvent.FiltersReset) }, width = 116.dp, height = 44.dp, surfaceColor = Color.White, borderColor = AppBorder, contentColor = AppPrimary)
            }
        }
    }
}

@Composable private fun QaDropdown(label: String, value: String, options: List<String>, modifier: Modifier, onSelected: (String) -> Unit) =
    AppLabeledDropdownField(
        label = label,
        value = value,
        options = options,
        onSelected = onSelected,
        modifier = modifier,
        allOptionLabel = when (label) {
            "Status" -> "All Statuses"
            "Created By" -> "All Users"
            else -> "All"
        }
    )

@Composable private fun QuickAnnouncementRowView(row: QuickAnnouncementRow, onView: () -> Unit, onEdit: () -> Unit, onDuplicate: () -> Unit, onEndNow: () -> Unit, onDelete: () -> Unit, actionsEnabled: Boolean) {
    var expanded by remember { mutableStateOf(false) }
    Row(
        Modifier
            .fillMaxWidth()
            .onViewDoubleClick { if (actionsEnabled) onView() }
            .heightIn(min = 52.dp)
            .padding(horizontal = 24.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        AppText(row.id, Modifier.weight(.85f), color = AppNavy, fontWeight = FontWeight.Normal)
        AppText(row.message, Modifier.weight(2.15f), color = AppNavy, fontWeight = FontWeight.Normal)
        AppText(row.displayFrom, Modifier.weight(1.05f), color = AppNavy, fontWeight = FontWeight.Normal)
        AppText(row.displayUntil, Modifier.weight(1.05f), color = AppNavy, fontWeight = FontWeight.Normal)
        Box(Modifier.weight(.75f)) { Text(row.status, color = when (row.status) { "Active" -> AppSuccess; "Expired" -> AppNavy; else -> AppNavy }, fontSize = 14.sp, fontWeight = FontWeight.Normal) }
        Box(Modifier.weight(.95f), contentAlignment = Alignment.Center) {
                AdminListActionIcons(
                    onView = { expanded = false; if (actionsEnabled) onView() },
                    onEdit = { expanded = false; if (actionsEnabled && row.status != "Expired") onEdit() },
                    editEnabled = actionsEnabled && row.status != "Expired"
                ) {
                    IconButton(onClick = { if (actionsEnabled) expanded = true }, enabled = actionsEnabled, modifier = Modifier.size(32.dp)) {
                    Icon(painterResource(Res.drawable.more_vertical), "More actions", tint = AppNavy, modifier = Modifier.size(16.dp))
                }
                AdminRowActionMenu(
                    buildList {
                        add(listOf(AdminRowActionItem("Duplicate", { expanded = false; onDuplicate() }, painterResource(Res.drawable.file_spreadsheet))))
                        if (row.status == "Active") {
                            add(listOf(AdminRowActionItem("End Now", { expanded = false; onEndNow() }, painterResource(Res.drawable.circle_check))))
                        }
                        add(listOf(AdminRowActionItem("Delete", { expanded = false; onDelete() }, painterResource(Res.drawable.trash), labelColor = AppDanger, iconTint = AppDanger)))
                    },
                    { expanded = false }, expanded = expanded
                )
            }
        }
    }
}
